import { buildSearchParams, urlBuilder } from '@starjack/utils';
import { createEntityApi, type EntityEndpointBuilder } from '@starjack/api';
import {
    VoucherTemplateAdminDTO,
    VoucherTemplateAdminDetailDTO,
    CreateVoucherTemplateCommand,
    UpdateVoucherTemplateCommand,
    SetActiveCommand,
    VoucherFontDTO,
} from '../model/voucherTemplate.types';

const urls = {
    templates: urlBuilder('order', '/admin/voucher-templates'),
    fonts: urlBuilder('order', '/admin/voucher-fonts'),
};

export interface VoucherTemplateApiHooks {
    useGetVoucherTemplatesQuery: (params?: { liftOperatorId?: string }) => {
        data?: VoucherTemplateAdminDTO[];
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    useGetVoucherTemplateQuery: (
        id: string,
        options?: { skip?: boolean }
    ) => {
        data?: VoucherTemplateAdminDetailDTO;
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    usePostVoucherTemplateMutation: () => [
        (body: CreateVoucherTemplateCommand) => {
            unwrap: () => Promise<VoucherTemplateAdminDetailDTO>;
        },
        { isLoading: boolean; error?: unknown },
    ];
    usePutVoucherTemplateMutation: () => [
        (params: { id: string; body: UpdateVoucherTemplateCommand }) => {
            unwrap: () => Promise<VoucherTemplateAdminDetailDTO>;
        },
        { isLoading: boolean; error?: unknown },
    ];
    useDeleteVoucherTemplateMutation: () => [
        (id: string) => { unwrap: () => Promise<void> },
        { isLoading: boolean; error?: unknown },
    ];
    usePatchVoucherTemplateActiveMutation: () => [
        (params: { id: string; body: SetActiveCommand }) => {
            unwrap: () => Promise<void>;
        },
        { isLoading: boolean; error?: unknown },
    ];
    useGetVoucherFontsQuery: (params?: { liftOperatorId?: string }) => {
        data?: VoucherFontDTO[];
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    useDeleteVoucherFontMutation: () => [
        (id: string) => { unwrap: () => Promise<void> },
        { isLoading: boolean; error?: unknown },
    ];
}

export const voucherTemplateEndpoints = (builder: EntityEndpointBuilder) => ({
    getVoucherTemplates: builder.query<
        VoucherTemplateAdminDTO[],
        { liftOperatorId?: string } | void
    >({
        query: (params) => {
            const p = params || {};
            const searchParams = buildSearchParams({
                liftOperatorId: p.liftOperatorId,
            });
            return `${urls.templates}${searchParams}`;
        },
        providesTags: ['VOUCHER_TEMPLATES'],
    }),

    getVoucherTemplate: builder.query<VoucherTemplateAdminDetailDTO, string>({
        query: (id) => `${urls.templates}/${id}`,
        providesTags: ['VOUCHER_TEMPLATES'],
    }),

    postVoucherTemplate: builder.mutation<
        VoucherTemplateAdminDetailDTO,
        CreateVoucherTemplateCommand
    >({
        query: (body) => ({
            url: urls.templates,
            method: 'POST',
            body,
        }),
        invalidatesTags: ['VOUCHER_TEMPLATES'],
    }),

    putVoucherTemplate: builder.mutation<
        VoucherTemplateAdminDetailDTO,
        { id: string; body: UpdateVoucherTemplateCommand }
    >({
        query: ({ id, body }) => ({
            url: `${urls.templates}/${id}`,
            method: 'PUT',
            body,
        }),
        invalidatesTags: ['VOUCHER_TEMPLATES'],
    }),

    deleteVoucherTemplate: builder.mutation<void, string>({
        query: (id) => ({
            url: `${urls.templates}/${id}`,
            method: 'DELETE',
            body: undefined,
        }),
        invalidatesTags: ['VOUCHER_TEMPLATES'],
    }),

    patchVoucherTemplateActive: builder.mutation<
        void,
        { id: string; body: SetActiveCommand }
    >({
        query: ({ id, body }) => ({
            url: `${urls.templates}/${id}/active`,
            method: 'PATCH',
            body,
        }),
        invalidatesTags: ['VOUCHER_TEMPLATES'],
    }),

    getVoucherFonts: builder.query<
        VoucherFontDTO[],
        { liftOperatorId?: string } | void
    >({
        query: (params) => {
            const p = params || {};
            const searchParams = buildSearchParams({
                liftOperatorId: p.liftOperatorId,
            });
            return `${urls.fonts}${searchParams}`;
        },
        providesTags: ['VOUCHER_FONTS'],
    }),

    deleteVoucherFont: builder.mutation<void, string>({
        query: (id) => ({
            url: `${urls.fonts}/${id}`,
            method: 'DELETE',
            body: undefined,
        }),
        invalidatesTags: ['VOUCHER_FONTS'],
    }),
});

export const voucherTemplateApi = createEntityApi({
    tagTypes: ['VOUCHER_TEMPLATES', 'VOUCHER_FONTS'],
    endpoints: voucherTemplateEndpoints,
});

export const useGetVoucherTemplatesQuery: VoucherTemplateApiHooks['useGetVoucherTemplatesQuery'] =
    voucherTemplateApi.useGetVoucherTemplatesQuery;
export const useGetVoucherTemplateQuery: VoucherTemplateApiHooks['useGetVoucherTemplateQuery'] =
    voucherTemplateApi.useGetVoucherTemplateQuery;
export const useLazyGetVoucherTemplateQuery =
    voucherTemplateApi.useLazyGetVoucherTemplateQuery;
export const usePostVoucherTemplateMutation: VoucherTemplateApiHooks['usePostVoucherTemplateMutation'] =
    voucherTemplateApi.usePostVoucherTemplateMutation;
export const usePutVoucherTemplateMutation: VoucherTemplateApiHooks['usePutVoucherTemplateMutation'] =
    voucherTemplateApi.usePutVoucherTemplateMutation;
export const useDeleteVoucherTemplateMutation: VoucherTemplateApiHooks['useDeleteVoucherTemplateMutation'] =
    voucherTemplateApi.useDeleteVoucherTemplateMutation;
export const usePatchVoucherTemplateActiveMutation: VoucherTemplateApiHooks['usePatchVoucherTemplateActiveMutation'] =
    voucherTemplateApi.usePatchVoucherTemplateActiveMutation;
export const useGetVoucherFontsQuery: VoucherTemplateApiHooks['useGetVoucherFontsQuery'] =
    voucherTemplateApi.useGetVoucherFontsQuery;
export const useDeleteVoucherFontMutation: VoucherTemplateApiHooks['useDeleteVoucherFontMutation'] =
    voucherTemplateApi.useDeleteVoucherFontMutation;
